import React, { useState, useMemo } from 'react';
import { DefaultCard, colors } from 'design-system';
import {
  Grid,
  makeStyles,
  createStyles,
  Button,
  useTheme,
  useMediaQuery,
} from '@material-ui/core';
import Collapse from '@material-ui/core/Collapse';

import InstructionsDialogue from '../InstructionsDialogue';
import InstructionsLayout from './InstructionsLayout';

const useStyles = makeStyles(() =>
  createStyles({
    collapseWrapper: {
      overflow: 'hidden',
      transition: 'max-height 0.15s ease-out',
    },
    linkStyle: {
      color: `${colors.bright_turquoise}`,
      fontWeight: 'bold',
      fontSize: '12px',
      lineHeight: '18px',
      display: 'flex',
      alignItems: 'center',
      textAlign: 'center',
      textTransform: 'none',
    },
  }),
);

export type InstructionsCardProps = {
  children?: React.ReactNode;
  title: string;
  question: string;
  dialogTitle: string;
  answer: string;
  imagePath: string;
};

const InstructionsCard = ({
  children,
  title,
  question,
  dialogTitle,
  imagePath,
  answer,
}: InstructionsCardProps): JSX.Element => {
  const classes = useStyles();
  const [isOpen, toggleIsOpen] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const collapseIsOpen = useMemo(() => {
    return isOpen && isMobile;
  }, [isMobile, isOpen]);

  const dialogueIsOpen = useMemo(() => {
    return isOpen && !isMobile;
  }, [isMobile, isOpen]);

  return (
    <DefaultCard
      id="sales-web-inspection-input-instruction-card"
      data-testid="instructionCard"
      title={title}
      forcedMinHeight={170}
    >
      <Grid container spacing={2}>
        <Grid item xs={12}>
          {children}
        </Grid>
        <Grid
          item
          xs={12}
          container
          alignItems="center"
          justifyContent="center"
        >
          <Button
            className={classes.linkStyle}
            data-testid="toggleButton"
            onClick={(): void => toggleIsOpen(!isOpen)}
          >
            {question}
          </Button>
        </Grid>

        {/* The collapse with instructions */}
        {isMobile && (
          <Grid item xs={12} className={classes.collapseWrapper}>
            <Collapse in={collapseIsOpen} data-testid="exampleImage">
              <InstructionsLayout imagePath={imagePath} information={answer} />
            </Collapse>
          </Grid>
        )}

        {/* The dialogue with instructions */}
        <InstructionsDialogue
          data-testid="example-dialogue"
          open={dialogueIsOpen}
          onCloseRequested={(): void => toggleIsOpen(!isOpen)}
          title={dialogTitle}
        >
          <InstructionsLayout imagePath={imagePath} information={answer} />
        </InstructionsDialogue>
      </Grid>
    </DefaultCard>
  );
};

export default InstructionsCard;
